import {PrismicNextImage} from '@prismicio/next/pages'
import {motion, useInView} from 'framer-motion'
import type {CarouselSliceDoubleImageCarousel} from 'prismicio-types'
import {useRef} from 'react'
import {Container} from 'src/common/Container'

type DoubleImageCarouselProps = {
  slice: CarouselSliceDoubleImageCarousel & {
    slice_type: string
    variation: string
  }
  componentIndex: number
  pageUid: string | null
}

export const DoubleImageCarousel = ({slice}: DoubleImageCarouselProps) => {
  const firstRowImages = slice.primary.images_first_row || []
  const secondRowImages = slice.primary.images_second_row || []
  const duration = slice.primary.duration || 8
  const ref = useRef(null)

  const isInView = useInView(ref, {
    amount: 0.2, // Trigger when 20% of the component is visible
    once: false, // Only trigger once
  })

  // Animation variants for left to right slide movement
  const leftToRightVariants = {
    hidden: {x: '-100%'},
    visible: {
      x: 0,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  }

  // Animation variants for right to left slide movement
  const rightToLeftVariants = {
    hidden: {x: '100%'},
    visible: {
      x: 0,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  }

  const duplicatedFirstRow = [...firstRowImages, ...firstRowImages]
  const duplicatedSecondRow = [...secondRowImages, ...secondRowImages]

  return (
    <Container
      className="relative !py-0 md:!pb-20 md:!pt-10"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor={slice.primary.background_color || 'Moss White'}
      containerRef={ref}
    >
      <div className="mx-auto max-w-[2000px] overflow-hidden">
        <div className="hidden md:flex md:flex-col md:gap-6">
          <motion.div
            className="flex h-[clamp(200px,20vw,400px)] gap-6"
            variants={leftToRightVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {firstRowImages.map((item, index) => (
              <div key={`desktop-first-${index}`} className="h-full rounded-[4px] bg-grey-150">
                <PrismicNextImage field={item.image} className="size-full rounded-[4px] object-cover" fallbackAlt="" />
              </div>
            ))}
          </motion.div>
          <motion.div
            className="flex h-[clamp(200px,20vw,400px)] gap-6"
            variants={rightToLeftVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {secondRowImages.map((item, index) => (
              <div key={`desktop-second-${index}`} className="h-full  rounded-[4px] bg-grey-150">
                <PrismicNextImage field={item.image} className="size-full rounded-[4px] object-cover" fallbackAlt="" />
              </div>
            ))}
          </motion.div>
        </div>

        <div className="flex max-w-[100vw] flex-col gap-4 overflow-hidden md:hidden">
          <div className="flex w-max animate-scroll-left gap-4" style={{animationDuration: `${duration}s`}}>
            {duplicatedFirstRow.map((item, index) => (
              <PrismicNextImage
                key={`image-row-1-${index}`}
                field={item.image}
                className="h-[200px] w-min rounded-[4px] object-contain"
                fallbackAlt=""
              />
            ))}
          </div>
          <div className="flex w-max animate-scroll-right gap-4" style={{animationDuration: `${duration}s`}}>
            {duplicatedSecondRow.map((item, index) => (
              <PrismicNextImage
                key={`image-row-2-${index}`}
                field={item.image}
                className="h-[200px] w-min rounded-[4px] object-contain"
                fallbackAlt=""
              />
            ))}
          </div>
        </div>
      </div>
    </Container>
  )
}
